import { useState } from "react";
import style from "./dashboard.module.scss";
import Layout from "components/layout/Layout";
import { DashboardTableRow } from "components/dashboardTableRow/dashboardTableRow";
import { Filter } from "components/UI/filter/filter";
import { SearchInput } from "components/UI/searchInput/searchInput";
import { RoutePaths } from "app/routing/routing";
import { useHistory } from "react-router-dom";
import { useGetCompanies } from "../../actions/companyActions";
import NoDataMessage from "components/noDataBlock/noDataMessage";
import SingleFilterDropDown from "components/UI/singleFilterDropDown/singleFilterDropDown";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { LoadingContainer } from "components/UI/loadingContainer/loadingContainer";
import translations from "locales";

const sortOptions = [
  {
    label: "Client Number - Newest to Oldest",
    value: { orderBy: "clNo", sortOrder: "DESC" },
  },
  {
    label: "Client Number - Oldest to Newest",
    value: { orderBy: "clNo", sortOrder: "ASC" },
  },
  {
    label: "Client Name - Alphabetical (A to Z)",
    value: { orderBy: "clName", sortOrder: "ASC" },
  },
  {
    label: "Client Name - Alphabetical (Z to A)",
    value: { orderBy: "clName", sortOrder: "DESC" },
  },
];


export const Dashboard = () => {
  const history = useHistory();
  const [searchWord, setSearchWord] = useState("");
  const [orderBy, setOrderBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("");
  const lang = localStorage.getItem('lang') || 'en';
  
  const {
    data: companyData,
    error: companyDataError,
    mutate: mutateCompanies,
    noDataFound: noCompanyDataFound,
    isValidating: isValidating,
    size,
    setSize,
    hasMore,
  } = useGetCompanies({
    searchWord,
    orderBy,
    sortOrder,
    rowNumber: 100,
  });

  const onViewClick = (
    id: string,
    companyData: { clNo: number; clName: string }
  ) => {
    history.push({
      pathname: `${RoutePaths.Company}/${id}`,
      state: { company: companyData }
    });
  };

  const onSearch = async (value: string) => {
    resetData();
    setSearchWord(value);
  };

  const handleSortChange = (value: any) => {
    setOrderBy(value?.orderBy || "");
    setSortOrder(value?.sortOrder || "");
    resetData();
  };

  const resetData = () => {
    setSize(1);
    mutateCompanies();
  };

  const loadMore = () => {
    setSize(prevSize => prevSize + 1);
  };
  const [loader] = useInfiniteScroll(loadMore, hasMore, [orderBy, sortOrder, searchWord]);

  return (
    <Layout>
      <div className={style.companies}>
        <h2>{lang === 'fr' ? translations.fr.your_clients : translations.en.your_clients}</h2>
      </div>
      <div className={style.filterButtons}>
        <SingleFilterDropDown 
          id="sortClients" 
          label={lang === 'fr' ? translations.fr.sort_clients : translations.en.sort_clients} 
          optionItems={lang === 'fr' ? translations.fr.sortOptionsFrDashBoard : sortOptions} 
          onOptionSelected={handleSortChange}
          mobileText={lang === 'fr' ? translations.fr.sort : translations.en.sort}
        />
        <SearchInput 
          id="clientSearch" 
          placeholder={lang === 'fr' ? translations.fr.try_work_permit : translations.en.try_work_permit}
          onChange={onSearch} 
        />
      </div>
      <div className={style.clientTableContainerWrapper}>
        <div className={style.clientTableContainer}>
          <table className={style.clientTable}>
            <thead>
              <tr>
                <th>{lang === 'fr' ? translations.fr.client_number : translations.en.client_number}</th>
                <th>{lang === 'fr' ? translations.fr.client_name : translations.en.client_name}</th>
                <th className={style.lawyer}>{lang === 'fr' ? translations.fr.lawyer : translations.en.lawyer}</th>
                <th className={style.cases}>{lang === 'fr' ? translations.fr.total_matter : translations.en.total_matter}</th>
              </tr>
            </thead>
            <tbody id={"dashboard-table"}>
              {!noCompanyDataFound &&  !isValidating && companyData?.map((rowData) => (
                <DashboardTableRow
                  key={rowData.clId}
                  number={rowData.clNo}
                  company={rowData.clName}
                  responsibleLawyer={rowData.responsibleLawyer}
                  cases={rowData.liveMatters}
                  onClick={() => onViewClick(rowData.clId, {
                    clNo: rowData.clNo,
                    clName: rowData.clName
                  })}
                />
              ))}
              {isValidating && companyData && size > 1 && (
                <tr>
                  <td colSpan={4}>
                    <span>Loading...</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div ref={loader} style={{ height: '1px', backgroundColor: 'transparent' }} />
          {size === 1 && isValidating && <LoadingContainer loadingLabel="Loading companies..." />}
          {noCompanyDataFound && !isValidating && (
            <NoDataMessage
              className={style.noDataMessage}
              title="No Clients Found!"
              description="Please reach out to your primary contact at the firm if you need assistance."
            />
          )}
        </div>
      </div>
    </Layout>
  );

};
